import { assertBoundedJson, validateMetricQuestions, validateReportMetrics } from '@arnold/core';
import { QuestionType } from '../../../generated/hooks';
import { ColumnType } from '../components/AnswerGroupTable/types';
import type { ReportQuestion } from '../types';
import type { ReportDefinitionDocument } from './types';

type ObjectValue = Record<string, any>;
const object = (value: unknown): value is ObjectValue => !!value && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const id = (value: unknown): value is string => typeof value === 'string' && /^[\w-]{1,100}$/.test(value);
const text = (value: unknown): boolean =>
  object(value) && Object.values(value).every((v) => typeof v === 'string' && v.length <= 10000);
const requireValue = (condition: unknown, message: string) => {
  if (!condition) throw new Error(message);
};
const uniqueIds = (items: ObjectValue[]) => {
  requireValue(
    items.every((item) => object(item) && id(item.id)),
    'id',
  );
  requireValue(new Set(items.map((item) => item.id)).size === items.length, 'duplicateId');
};

/**
 * Validate untrusted persisted JSON before it reaches rendering or calculation. Never coerce or strip fields.
 * Bounds and metrics are checked by `@arnold/core` (shared with the API's metrics snapshot), the layout here.
 */
export function parseReportDefinition(value: unknown): ReportDefinitionDocument {
  assertBoundedJson(value);
  const document = value as ObjectValue;
  requireValue(object(document.pages) && object(document.pages.overview), 'pages');
  const components = document.pages.overview.components;
  requireValue(Array.isArray(components) && components.length <= 500, 'components');
  validateReportMetrics(document.metrics);
  uniqueIds(components);
  requireValue(
    components.filter((component: ObjectValue) => component.type === 'aiSummary').length <= 1,
    'duplicateAiSummary',
  );
  const metricIds = new Set(document.metrics.map((metric: ObjectValue) => metric.id));
  const binding = (source: ObjectValue) => {
    requireValue(object(source) && id(source.questionIndex), 'question');
    requireValue(
      source.statementIndex === undefined || (Number.isInteger(source.statementIndex) && source.statementIndex >= 0),
      'statement',
    );
  };
  components.forEach((component: ObjectValue) => {
    requireValue(
      component.width === undefined || ['full', 'half', 'third', 'quarter'].includes(component.width),
      'width',
    );
    switch (component.type) {
      case 'section':
        requireValue(text(component.label), 'label');
        break;
      case 'question':
        binding(component);
        break;
      case 'aiSummary':
        requireValue(component.width === undefined || component.width === 'full', 'width');
        requireValue(
          component.displayMode === undefined || ['collapsible', 'expanded'].includes(component.displayMode),
          'summaryDisplayMode',
        );
        break;
      case 'responseRate':
      case 'anonymityRate':
        break;
      case 'metric':
        requireValue(metricIds.has(component.metricId), 'metricReference');
        break;
      case 'table': {
        requireValue(text(component.label) && object(component.table), 'table');
        const table = component.table;
        requireValue(Array.isArray(table.columns) && table.columns.length > 0 && table.columns.length <= 50, 'columns');
        uniqueIds(table.columns);
        table.columns.forEach((column: ObjectValue) => {
          requireValue(
            Object.values(ColumnType).includes(column.type) &&
              ![ColumnType.NPS, ColumnType.MINI_NPS, ColumnType.SCORE].includes(column.type) &&
              typeof column.label === 'string',
            'column',
          );
          requireValue(column.translations === undefined || text(column.translations), 'label');
          ['width', 'minWidth', 'maxWidth'].forEach((key) =>
            requireValue(column[key] === undefined || (finite(column[key]) && column[key] > 0), 'width'),
          );
          if (column.metricId !== undefined) requireValue(metricIds.has(column.metricId), 'metricReference');
          if (column.questionIndex !== undefined) binding(column);
          if (['choices', 'text', 'themes', 'nps', 'miniNps', 'score', 'questionAnswerCount'].includes(column.type))
            binding(column);
          if (column.type === 'customMetric') {
            requireValue(metricIds.has(column.metricId), 'metricReference');
            requireValue(
              column.presentation === undefined ||
                ['nps', 'miniNps', 'score', 'percentage', 'number', 'distribution'].includes(column.presentation),
              'presentation',
            );
            const columnMetricType = document.metrics.find((metric: ObjectValue) => metric.id === column.metricId)
              ?.calculation.type;
            if (['nps', 'miniNps'].includes(column.presentation))
              requireValue(columnMetricType === 'nps', 'presentation');
            if (column.presentation === 'distribution')
              requireValue(['scaleBands', 'respondentSegment'].includes(columnMetricType), 'presentation');
          }
          requireValue(column.source === undefined || ['text', 'comment'].includes(column.source), 'source');
        });
        if (table.comparisonGroups !== undefined) {
          requireValue(Array.isArray(table.comparisonGroups) && table.comparisonGroups.length <= 100, 'comparisons');
          table.comparisonGroups.forEach((group: ObjectValue) => {
            requireValue(object(group), 'comparisons');
            if (group.type === 'baseline')
              requireValue(
                typeof group.id === 'string' &&
                  (/^team:\d+$/.test(group.id) ||
                    ['comparison:current-report', 'custom:unassigned'].includes(group.id)),
                'comparisons',
              );
            else {
              requireValue(
                group.type === 'custom' &&
                  text(group.label) &&
                  Array.isArray(group.teamIds) &&
                  group.teamIds.every((value: unknown) => typeof value === 'string') &&
                  Array.isArray(group.answerSelections) &&
                  group.answerSelections.length > 0,
                'comparisons',
              );
              group.answerSelections.forEach((selection: ObjectValue) =>
                requireValue(
                  object(selection) &&
                    id(selection.questionId) &&
                    typeof selection.optionId === 'string' &&
                    /^\d+$/.test(selection.optionId) &&
                    (selection.statementId === undefined ||
                      (typeof selection.statementId === 'string' && /^\d+$/.test(selection.statementId))),
                  'comparisons',
                ),
              );
            }
          });
        }
        if (table.nonEmptyTextColumnId !== undefined)
          requireValue(
            table.columns.some(
              (column: ObjectValue) =>
                column.id === table.nonEmptyTextColumnId && ['text', 'themes'].includes(column.type),
            ),
            'column',
          );
        requireValue(['teams', 'teamsAndParticipants', 'participants'].includes(table.rowMode), 'rows');
        requireValue(Number.isInteger(table.pageSize) && table.pageSize > 0 && table.pageSize <= 100, 'pageSize');
        ['sortingEnabled', 'searchEnabled', 'columnOrderingEnabled', 'columnResizeEnabled', 'horizontalScroll'].forEach(
          (key) => requireValue(typeof table[key] === 'boolean', 'table'),
        );
        break;
      }
      default:
        throw new Error('component');
    }
  });
  return value as ReportDefinitionDocument;
}

export function validateDefinitionQuestions(definition: ReportDefinitionDocument, questions: ReportQuestion[]): void {
  validateMetricQuestions(definition.metrics, questions);
  const check = (source: { questionIndex: string; statementIndex?: number }) => {
    const question = questions.find((item) => item.index === source.questionIndex);
    requireValue(question, 'question');
    requireValue(
      source.statementIndex === undefined || source.statementIndex < (question!.statementsLocal?.length ?? 0),
      'statement',
    );
    return question!;
  };
  definition.pages.overview.components.forEach((component) => {
    if (component.type === 'question')
      requireValue(
        [QuestionType.Select, QuestionType.Multiselect, QuestionType.Scale, QuestionType.Freetext].includes(
          check(component).type,
        ),
        'question',
      );
    if (component.type === 'table') {
      component.table.columns.forEach((column) => {
        if ('questionIndex' in column) {
          const question = check(column);
          if (column.type === ColumnType.CHOICES)
            requireValue(
              [QuestionType.Select, QuestionType.Multiselect, QuestionType.Scale].includes(question.type),
              'question',
            );
          if (column.type === ColumnType.TEXT || column.type === ColumnType.THEMES)
            requireValue(column.source === 'comment' || question.type === QuestionType.Freetext, 'source');
        }
      });
      if (component.table.responseQuestionIndex) check({ questionIndex: component.table.responseQuestionIndex });
      component.table.comparisonGroups?.forEach((group) => {
        if (group.type !== 'custom') return;
        group.answerSelections.forEach((selection) => {
          const question = check({
            questionIndex: selection.questionId,
            statementIndex: selection.statementId === undefined ? undefined : Number(selection.statementId),
          });
          requireValue(Number(selection.optionId) < (question.answersLocal?.length ?? 0), 'choices');
        });
      });
    }
  });
}
